import { z } from 'zod';

import { apiGet, apiPost } from '../client';
import type { BillingCycle, PlanTier } from '../../lib/plan';

/**
 * Stripe for the Dale plan: the card step of signup and "Tarjeta" on /pago.
 * The server prices the plan on file and only checks that `plan` and `cycle`
 * agree with it; Stripe's own page takes the card, and Stripe's webhook is
 * what confirms it. Nothing here ever sees card data.
 */

export const STRIPE_STATUS_QUERY_KEY = ['billing', 'stripe'];

/** The only page the owner is ever sent to; the server checks the same origin. */
export const STRIPE_CHECKOUT_ORIGIN = 'https://checkout.stripe.com';

export const StripeStatusSchema = z.object({
  configured: z.boolean(),
  /** The CRM stays on the card step while this is true. */
  cardRequired: z.boolean(),
  cardOnFile: z.boolean(),
  subscribed: z.boolean(),
  /** A checkout the owner started is still open at Stripe; absent reads as closed. */
  checkoutOpen: z.boolean().default(false),
  /** The free trial's end on file, running or over; null (or absent, on an older API) when there is none. */
  trialEndsAt: z.string().nullable().default(null),
});
export type StripeStatus = z.infer<typeof StripeStatusSchema>;

const StatusResponseSchema = z.object({ data: StripeStatusSchema });
const CheckoutResponseSchema = z.object({ data: z.object({ checkoutUrl: z.string().url() }) });

export type StripeCheckoutInput = { plan: PlanTier; cycle: BillingCycle };

export async function getStripeStatus(): Promise<StripeStatus> {
  return (await apiGet('/v1/business/billing/stripe', StatusResponseSchema)).data;
}

export async function createStripeCheckout(input: StripeCheckoutInput, idempotencyKey: string): Promise<{ checkoutUrl: string }> {
  return (await apiPost('/v1/business/billing/stripe/checkout', CheckoutResponseSchema, input, idempotencyKey)).data;
}

export function isStripeCheckoutUrl(value: string): boolean {
  try {
    const url = new URL(value);
    return url.protocol === 'https:' && url.origin === STRIPE_CHECKOUT_ORIGIN;
  } catch {
    return false;
  }
}

/**
 * Opens Stripe Checkout for the plan on file: a subscription for Base, Pro
 * or Max, or saving the card for Independientes. Leaves the page on success;
 * throws an ApiError (friendly Spanish message) when it cannot.
 */
export async function startStripeCheckout(input: StripeCheckoutInput, idempotencyKey: string = crypto.randomUUID()): Promise<void> {
  const { checkoutUrl } = await createStripeCheckout(input, idempotencyKey);
  if (!isStripeCheckoutUrl(checkoutUrl)) throw new Error('El enlace de pago no es válido. Intenta de nuevo.');
  window.location.assign(checkoutUrl);
}

/**
 * Cancelling the plan with the 30-day guarantee: within 30 days of the first
 * Stripe payment everything comes back and the plan ends now; after that the
 * plan stops renewing and stays until the paid period ends.
 */
export const GUARANTEE_QUERY_KEY = ['billing', 'stripe', 'guarantee'];

export const GuaranteeSchema = z.object({
  eligible: z.boolean(),
  until: z.string().nullable(),
  refundCents: z.number().int().nonnegative(),
  cancelAt: z.string().nullable(),
});
export type Guarantee = z.infer<typeof GuaranteeSchema>;

export const CancellationSchema = z.object({
  mode: z.enum(['refunded', 'at_period_end', 'canceled', 'none']),
  refundedCents: z.number().int().nonnegative(),
  endsAt: z.string().nullable(),
});
export type Cancellation = z.infer<typeof CancellationSchema>;

export async function getGuarantee(): Promise<Guarantee> {
  return (await apiGet('/v1/business/billing/stripe/guarantee', z.object({ data: GuaranteeSchema }))).data;
}

export async function cancelPlan(idempotencyKey: string = crypto.randomUUID()): Promise<Cancellation> {
  return (await apiPost('/v1/business/billing/stripe/cancel', z.object({ data: CancellationSchema }), {}, idempotencyKey)).data;
}
